import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { X, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { safeLocalStorage } from "@/lib/safeStorage";

export function VerificationReminderBanner() {
  const { user } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Check if banner was dismissed
  useEffect(() => {
    const dismissed = safeLocalStorage.getItem("verification-banner-dismissed");
    if (dismissed) {
      setIsDismissed(true);
    }
  }, []);

  // Fetch user profile to check verification status
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("email_verified_at, created_at")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const handleDismiss = () => {
    safeLocalStorage.setItem("verification-banner-dismissed", "true");
    setIsDismissed(true);
  };

  const handleResend = async () => {
    if (!user?.id) return;

    setIsResending(true);
    try {
      const { error } = await supabase.functions.invoke("resend-verification-email", {
        body: { userId: user.id },
      });

      if (error) throw error;

      toast.success("Verification email sent!");
    } catch (error: unknown) {
      console.error(error);
      toast.error("Failed to send verification email");
    } finally {
      setIsResending(false);
    }
  };

  // Don't show if dismissed, not logged in, already verified, or user created less than 24 hours ago
  if (
    isDismissed ||
    !user ||
    !profile ||
    profile.email_verified_at ||
    (profile.created_at &&
      new Date().getTime() - new Date(profile.created_at).getTime() < 24 * 60 * 60 * 1000)
  ) {
    return null;
  }

  return (
    <Alert className="relative border-primary/50 bg-primary/5">
      <Mail className="h-4 w-4" />
      <AlertDescription className="flex items-center justify-between gap-4 pr-8">
        <span className="text-sm">
          Don't forget to verify your email to upgrade your subscription
        </span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleResend} disabled={isResending}>
            {isResending ? "Sending..." : "Verify Now"}
          </Button>
        </div>
      </AlertDescription>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-6 w-6"
        onClick={handleDismiss}
      >
        <X className="h-4 w-4" />
      </Button>
    </Alert>
  );
}
